import { Flex, Form, Typography } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import { AnimatePresence, motion } from "motion/react"
import React from "react"
import { useTranslation } from "react-i18next"

import { budgetFormat } from "../../../../../../utils/number/budget-format.ts"
import {
  splitDaysIntoParts,
  totalDurationDays
} from "../../../../shared/duration-date-calculator.ts"
import type { TemplateDocumentFormData } from "../../../../types/template-document-form-data.tsx"

const { Text } = Typography

interface ScopeOfWorkBlockFooterProps {
  form: FormInstance<TemplateDocumentFormData>
}

/**
 * "Total" never silently reads a milestone with no duration as 0 — a
 * defined-vs-missing split (via the shared `duration-date-calculator.ts`
 * engine, which is also where "years" support now lives, replacing this
 * component's own inline unit-to-days switch) so "23 days" can never look
 * complete when 7 of 10 milestones were never given a duration at all.
 */
export function ScopeOfWorkBlockFooter({ form }: ScopeOfWorkBlockFooterProps) {
  const { t } = useTranslation()
  const phases = Form.useWatch("phases", form) || []

  const totalBudget = phases.reduce(
    (sum: number, item) => sum + +(item?.budget || 0),
    0
  )

  const formatDurationParts = (totalDays: number) => {
    const { years, months, days } = splitDaysIntoParts(totalDays)

    const parts: string[] = []
    if (years > 0) parts.push(`${years} ${years === 1 ? "year" : "years"}`)
    if (months > 0) parts.push(`${months} ${months === 1 ? "month" : "months"}`)
    if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`)

    return parts.join(", ")
  }

  const calculateTotalTimeline = () => {
    if (!phases.length) return "—"

    const { totalDays, definedCount, missingCount } = totalDurationDays(
      phases.map((phase) => ({
        duration: phase?.duration_days,
        durationUnit: phase?.duration_type
      }))
    )

    if (definedCount === 0) {
      return missingCount > 0
        ? t("documentTemplates.scopeOfWork.footer.noneDefined")
        : "—"
    }

    const definedText = t("documentTemplates.scopeOfWork.footer.defined", {
      duration: formatDurationParts(totalDays)
    })

    if (missingCount === 0) return formatDurationParts(totalDays)

    return [
      t("documentTemplates.scopeOfWork.footer.incomplete"),
      [
        definedText,
        t("documentTemplates.scopeOfWork.footer.missingDuration", {
          count: missingCount
        })
      ].join(" · ")
    ].join(" — ")
  }

  return (
    <Flex
      align={"center"}
      className={
        "p-3 mt-2 border bg-[var(--ant-table-footer-bg)] border-border rounded-card"
      }
    >
      <div className="flex-1">
        <Text strong>Total</Text>
      </div>
      <div style={{ width: 270 }}>
        <Text strong>
          <AnimatedText value={calculateTotalTimeline()} />
        </Text>
      </div>
      <div style={{ width: 225 }}>
        <Text strong ellipsis className="truncate max-w-40">
          {budgetFormat(totalBudget)}
        </Text>
      </div>
      <div style={{ width: 50 }} />
    </Flex>
  )
}

function AnimatedText({ value }: { value: string }) {
  return (
    <span style={{ display: "inline-flex", overflow: "hidden" }}>
      <AnimatePresence mode="wait">
        <motion.span
          key={value}
          style={{ display: "inline-flex" }}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {value.split("").map((char, i) => (
            <motion.span
              key={i}
              style={{ display: "inline-block" }}
              variants={{
                hidden: { opacity: 0, y: -16 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.2, delay: i * 0.03 }
                },
                exit: {
                  opacity: 0,
                  y: 16,
                  transition: { duration: 0.15, delay: i * 0.02 }
                }
              }}
            >
              {char === " " ? "\u00A0" : char}
            </motion.span>
          ))}
        </motion.span>
      </AnimatePresence>
    </span>
  )
}
